import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { QuantileCells, ibamaCasesHref } from '@/pages/ibama/OverviewPage';
import { fetchIbamaTimeline } from '@/services/ibama';
import type { AnswerOut, IbamaInterval } from '@/services/types';

/**
 * How long an IBAMA auto takes, step by step, and how mature each cohort is.
 *
 * Four intervals over the rows carrying both dates; the first is on the
 * autos master (every auto issued), the other three on the debt ledger,
 * and each panel names its basis. No settled-share trend: it would confound
 * collection with cohort maturity, so it is refused beside the maturity
 * table.
 */
export function IbamaTimelinePage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'timeline'],
    queryFn: fetchIbamaTimeline,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:timeline.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:timeline.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ibama:timeline.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ibama/export/timeline" label={t('ibama:timeline.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        {data.steps.map((step, index) => (
          <StepPanel key={step.value?.step ?? index} answer={step} />
        ))}
      </div>

      <FieldGuide i18nKey="ibama:timeline.guide" level={2} />

      <section className="rounded-lg border bg-card p-4" data-cohorts>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ibama:timeline.cohorts.title')}
        </h2>
        <Answer answer={data.cohorts} help={t('ibama:timeline.cohorts.help')}>
          {(rows) => (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ibama:timeline.cohorts.year')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:timeline.cohorts.cases')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:timeline.cohorts.firstDecided')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:timeline.cohorts.boardDated')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ibama:timeline.cohorts.settled')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.year} className="border-t" data-cohort={c.year}>
                      <td className="px-3 py-2 tabular-nums text-foreground">{c.year}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <Link
                          to={ibamaCasesHref({ year: c.year })}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {number.format(c.cases)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(c.first_decided)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(c.first_decided_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(c.board_dated)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(c.settled)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(c.settled_share)})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Answer>
      </section>

      <RefusalCard title={t('ibama:timeline.trend.title')} answer={data.outcome_trend} />
    </div>
  );
}

function StepPanel({ answer }: { answer: AnswerOut<IbamaInterval> }) {
  const { t, i18n } = useTranslation(['ibama']);
  const number = new Intl.NumberFormat(i18n.language);
  const step = answer.value?.step ?? 'notice_to_first';
  return (
    <section className="rounded-lg border bg-card p-4" data-step={step}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(`ibama:timeline.steps.${step}.title`)}
      </h2>
      <Answer answer={answer} help={t(`ibama:timeline.steps.${step}.help`)}>
        {(interval) => (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground" data-basis={interval.basis}>
              {t(`ibama:timeline.basis.${interval.basis}`)}
            </p>
            <QuantileCells
              summary={interval.days}
              format={(v) => t('ibama:timeline.days', { n: number.format(v) })}
            />
            <p className="text-xs text-muted-foreground">
              {t('ibama:timeline.rows', { n: number.format(interval.rows_with_both_ends) })}
              {interval.negative > 0
                ? ` · ${t('ibama:timeline.negative', { n: number.format(interval.negative) })}`
                : ''}
            </p>
          </div>
        )}
      </Answer>
    </section>
  );
}
